import { useState, useCallback } from 'react';

interface StepHandler {
  handleNext?: (formData: Record<string, any>) => boolean | Promise<boolean>;
  handleBack?: (formData: Record<string, any>) => boolean | Promise<boolean>;
  handleSubmit?: (formData: Record<string, any>) => boolean | Promise<boolean>;
  [key: string]: any;
}

interface UseStepNavigationProps {
  totalSteps: number;
  handlers: StepHandler[];
}

export const useStepNavigation = ({
  totalSteps,
  handlers,
}: UseStepNavigationProps) => {
  const [currentStep, setCurrentStep] = useState(0);

  const handleAction = useCallback(
    async (action: string, formData: Record<string, any>): Promise<boolean> => {
      const currentHandler = handlers[currentStep];

      if (!currentHandler) {
        console.warn(`No handler found for step ${currentStep}`);
        return false;
      }

      switch (action) {
        case 'next':
          if (currentHandler.handleNext) {
            const result = await currentHandler.handleNext(formData);
            if (result && currentStep < totalSteps - 1) {
              setCurrentStep(prev => prev + 1);
            }
            return result;
          }
          return false;

        case 'back':
          if (currentHandler.handleBack) {
            const result = await currentHandler.handleBack(formData);
            if (result && currentStep > 0) {
              setCurrentStep(prev => prev - 1);
            }
            return result;
          }
          return false;

        case 'submit':
          if (currentHandler.handleSubmit) {
            return await currentHandler.handleSubmit(formData);
          }
          return false;

        default:
          const customHandler = currentHandler[action];
          if (typeof customHandler === 'function') {
            return await customHandler(formData);
          }
          console.warn(`Unknown action: ${action}`);
          return false;
      }
    },
    [currentStep, handlers, totalSteps]
  );

  const goToStep = (step: number) => {
    if (step >= 0 && step < totalSteps) {
      setCurrentStep(step);
    }
  };

  const resetNavigation = () => {
    setCurrentStep(0);
  };

  return {
    currentStep,
    setCurrentStep,
    handleAction,
    goToStep,
    resetNavigation,
  };
};
